import React, { useEffect, useState } from "react";
import { Plus, Trash2, Receipt, Paperclip, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cardCls, Btn, Input, Select, Field, EmptyState } from "@/components/admin/ui";
import FileUpload from "@/components/admin/FileUpload";

const CATS = ["Operations", "Marketing", "Payroll", "Rent", "Supplies", "Technology", "Professional Fees", "Other"];
const EMPTY = { description: "", amount: "", category: "Operations", venture_id: "", date: "", receipt_file_id: null };

export default function ExpensesCard({ ventures = [], onChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/expenses").then(({ data }) => { setItems(data); onChange && onChange(data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.description || !form.amount) { toast.error("Description and amount required"); return; }
    try {
      await api.post("/admin/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense recorded"); setForm(EMPTY); setReceiptName(""); setAdding(false); load();
    } catch { toast.error("Could not save expense"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/expenses/${id}`); load(); }
    catch { toast.error("Could not delete expense"); }
  };
  const [receiptName, setReceiptName] = useState("");
  const downloadReceipt = async (fid) => {
    try {
      const res = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open receipt"); }
  };
  const vName = (id) => ventures.find((v) => v.id === id)?.name || "Group";
  const total = items.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div data-testid="expenses-card" className={`${cardCls} p-6`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Receipt size={20} className="text-bd-gold" />
          <h2 className="font-serif text-xl text-bd-ivory">Expenses ledger</h2>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-bd-ivory/60">Total <span className="text-bd-ivory">£{total.toLocaleString("en-GB")}</span></span>
          <Btn data-testid="expense-add" onClick={() => setAdding((v) => !v)}><Plus size={14} /> Add</Btn>
        </div>
      </div>

      {adding && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5 items-end">
          <div className="md:col-span-2"><Field label="Description"><Input data-testid="expense-description" value={form.description} onChange={set("description")} /></Field></div>
          <Field label="Amount (£)"><Input data-testid="expense-amount" type="number" value={form.amount} onChange={set("amount")} /></Field>
          <Field label="Category"><Select value={form.category} onChange={set("category")}>{CATS.map((c) => <option key={c}>{c}</option>)}</Select></Field>
          <Field label="Venture"><Select value={form.venture_id} onChange={set("venture_id")}><option value="">Group</option>{ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</Select></Field>
          <div className="md:col-span-5 flex flex-wrap gap-3 items-center">
            <Input type="date" className="max-w-[180px]" value={form.date} onChange={set("date")} />
            <FileUpload value={null} visibility="private" accept=".pdf,.png,.jpg,.jpeg" preview={false} label="Attach receipt"
              onChange={(url, data) => { setForm((f) => ({ ...f, receipt_file_id: data.id })); setReceiptName(data.original_filename); }} />
            {receiptName && <span className="text-xs text-bd-gold">{receiptName}</span>}
            <Btn data-testid="expense-save" onClick={save}>Save expense</Btn>
          </div>
        </div>
      )}

      {items.length === 0 ? <EmptyState>No expenses recorded yet.</EmptyState> : (
        <div className="divide-y divide-white/5">
          {items.map((e) => (
            <div key={e.id} className="flex items-center gap-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-bd-ivory truncate">{e.description}</p>
                <p className="text-xs text-bd-ivory/40">{e.category} · {vName(e.venture_id)}{e.date ? ` · ${e.date}` : ""}</p>
              </div>
              <span className="text-sm text-bd-ivory/80">£{Number(e.amount).toLocaleString("en-GB")}</span>
              {e.receipt_file_id && <button onClick={() => downloadReceipt(e.receipt_file_id)} title="View receipt" className="p-2 text-bd-ivory/40 hover:text-bd-gold"><Paperclip size={15} /></button>}
              <button onClick={() => remove(e.id)} className="p-2 text-bd-ivory/40 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
